import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ShareSidebar } from '@/components/ui/share-sidebar';
import { SmartTag } from '@/components/ui/tag';

interface VideoItem {
  id: string;
  title: string;
  description: string;
  youtubeId: string;
  category: string;
  tags: string[];
  url_slug?: string;
}

interface VideoGalleryProps {
  videos: VideoItem[];
  className?: string;
  gridCols?: 'default' | 'dense';
  onTagClick?: (tag: string) => void;
  selectedTags?: string[];
  onVideoClick?: (video: VideoItem) => void;
  selectedVideo?: VideoItem | null;
  onCloseVideo?: () => void;
}

const VideoGallery = ({ 
  videos, 
  className = '', 
  gridCols = 'default', 
  onTagClick, 
  selectedTags = [],
  onVideoClick,
  selectedVideo: externalSelectedVideo,
  onCloseVideo
}: VideoGalleryProps) => {
  const [internalSelectedVideo, setInternalSelectedVideo] = useState<VideoItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Use external selected video if provided, otherwise use internal state
  const selectedVideo = externalSelectedVideo || internalSelectedVideo;

  const openVideo = (video: VideoItem) => {
    if (onVideoClick) {
      onVideoClick(video);
    } else {
      setInternalSelectedVideo(video);
    }
    // Find the index in the original videos array for proper navigation
    const originalIndex = videos.findIndex(v => v.id === video.id);
    setCurrentIndex(originalIndex);
  };

  const closeVideo = () => {
    if (onCloseVideo) {
      onCloseVideo();
    } else {
      setInternalSelectedVideo(null);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const newVideo = videos[newIndex];
      if (onVideoClick) {
        onVideoClick(newVideo);
      } else {
        setInternalSelectedVideo(newVideo);
      }
    }
  };

  const goToNext = () => {
    if (currentIndex < videos.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const newVideo = videos[newIndex];
      if (onVideoClick) {
        onVideoClick(newVideo);
      } else {
        setInternalSelectedVideo(newVideo);
      }
    }
  };

  const getThumbnailUrl = (youtubeId: string) => {
    // Extract video ID from embed URL or use as-is if it's already a video ID
    let videoId = youtubeId;
    if (youtubeId.includes('/embed/')) {
      videoId = youtubeId.split('/embed/')[1]?.split('?')[0] || youtubeId;
    } else if (youtubeId.includes('watch?v=')) {
      videoId = youtubeId.split('v=')[1]?.split('&')[0] || youtubeId;
    } else if (youtubeId.includes('youtube.com/shorts/')) {
      videoId = youtubeId.split('shorts/')[1]?.split('?')[0] || youtubeId;
    }
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  return (
    <>

      <div className={`grid gap-6 ${
        gridCols === 'dense' 
          ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5' 
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      } ${className}`}>
        {videos.map((video) => (
          <Card 
            key={video.id} 
            className="group overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-[1.02] cursor-pointer"
            onClick={() => openVideo(video)}
          >
            <CardContent className="p-0">
              <div className="relative aspect-video bg-muted">
                <img 
                  src={getThumbnailUrl(video.youtubeId)}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-all duration-300">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-6 h-6 text-primary-foreground ml-1" fill="currentColor" />
                  </div>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-medium text-sm leading-tight text-foreground line-clamp-2 mb-2">
                  {video.title}
                </h3>
                <div className="flex flex-wrap gap-1">
                  {video.tags.map((tag, index) => (
                    <SmartTag 
                      key={index} 
                      className={`text-xs cursor-pointer hover:opacity-80 transition-all duration-200 ${
                        selectedTags.includes(tag) ? 'ring-1 ring-primary ring-offset-1' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTagClick?.(tag);
                      }}
                    >
                      {tag}
                    </SmartTag>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={closeVideo}>
        <DialogContent 
          className="max-w-5xl w-full max-h-[85vh] overflow-hidden p-0"
          onPointerDownOutside={(e) => {
            // Prevent closing when clicking on share sidebar
            const target = e.target as Element;
            if (target.closest('[data-share-sidebar]')) {
              e.preventDefault();
            }
          }}
        >
          {selectedVideo && (
            <div className="flex flex-col">

              {/* Video Player */}
              <div className="relative aspect-video bg-black">
                <iframe
                  src={selectedVideo.youtubeId}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Content Below Video */}
              <div className="p-4 md:p-6 bg-background">
                {/* Mobile Layout - Vertical */}
                <div className="md:hidden space-y-4">
                  {/* Navigation Thumbnails - Horizontal Row at Top */}
                  <div className="flex justify-center gap-4">
                    {/* Previous Video Thumbnail */}
                    {currentIndex > 0 && (
                      <button
                        onClick={goToPrevious}
                        className="group"
                      >
                        <div className="w-16 h-10 relative overflow-hidden rounded border-2 border-transparent group-hover:border-primary transition-colors">
                          <img 
                            src={getThumbnailUrl(videos[currentIndex - 1].youtubeId)}
                            alt={videos[currentIndex - 1].title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <ChevronLeft className="w-3 h-3 text-white" />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 text-center">Previous</p>
                      </button>
                    )}

                    {/* Next Video Thumbnail */}
                    {currentIndex < videos.length - 1 && (
                      <button
                        onClick={goToNext}
                        className="group"
                      >
                        <div className="w-16 h-10 relative overflow-hidden rounded border-2 border-transparent group-hover:border-primary transition-colors">
                          <img 
                            src={getThumbnailUrl(videos[currentIndex + 1].youtubeId)}
                            alt={videos[currentIndex + 1].title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <ChevronRight className="w-3 h-3 text-white" />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 text-center">Next</p>
                      </button>
                    )}
                  </div>

                  {/* Title and Description - Full Width Below Navigation */}
                  <div className="text-center">
                    <h3 className="font-bold text-lg mb-3 text-foreground leading-tight">
                      {selectedVideo.title}
                    </h3>
                    <div className="flex flex-wrap justify-center gap-1 mb-3">
                      {selectedVideo.tags.map((tag, index) => (
                        <SmartTag 
                          key={index} 
                          className={`text-xs cursor-pointer hover:opacity-80 transition-all duration-200 ${
                            selectedTags.includes(tag) ? 'ring-2 ring-primary ring-offset-2' : ''
                          }`}
                          onClick={() => onTagClick?.(tag)}
                        >
                          {tag}
                        </SmartTag>
                      ))}
                    </div>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {selectedVideo.description}
                    </p>
                  </div>
                </div>

                {/* Desktop Layout - Horizontal */}
                <div className="hidden md:flex items-start gap-6">
                  {/* Previous Video Thumbnail */}
                  {currentIndex > 0 && (
                    <button
                      onClick={goToPrevious}
                      className="flex-shrink-0 group"
                    >
                      <div className="w-20 h-12 relative overflow-hidden rounded border-2 border-transparent group-hover:border-primary transition-colors">
                        <img 
                          src={getThumbnailUrl(videos[currentIndex - 1].youtubeId)}
                          alt={videos[currentIndex - 1].title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                          <ChevronLeft className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 text-center">Previous</p>
                    </button>
                  )}

                  {/* Title and Description */}
                  <div className="flex-1 text-center">
                    <h3 className="font-bold text-xl mb-3 text-foreground">
                      {selectedVideo.title}
                    </h3>
                    <div className="flex flex-wrap justify-center gap-1 mb-3">
                      {selectedVideo.tags.map((tag, index) => (
                        <SmartTag 
                          key={index} 
                          className={`text-xs cursor-pointer hover:opacity-80 transition-all duration-200 ${
                            selectedTags.includes(tag) ? 'ring-2 ring-primary ring-offset-2' : ''
                          }`}
                          onClick={() => onTagClick?.(tag)}
                        >
                          {tag}
                        </SmartTag>
                      ))}
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {selectedVideo.description}
                    </p>
                  </div>

                  {/* Next Video Thumbnail */}
                  {currentIndex < videos.length - 1 && (
                    <button
                      onClick={goToNext}
                      className="flex-shrink-0 group"
                    >
                      <div className="w-20 h-12 relative overflow-hidden rounded border-2 border-transparent group-hover:border-primary transition-colors">
                        <img 
                          src={getThumbnailUrl(videos[currentIndex + 1].youtubeId)}
                          alt={videos[currentIndex + 1].title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                          <ChevronRight className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 text-center">Next</p>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Sidebar - Only shows when popup is open */}
      <ShareSidebar
        isOpen={!!selectedVideo}
        shareUrl={`${window.location.origin}/getting-started?slug=${selectedVideo?.url_slug || `video-${selectedVideo?.id}`}`}
        shareTitle={selectedVideo?.title || 'Getting Started Video'}
        shareDescription={selectedVideo?.description || 'Check out this helpful tutorial video!'}
      />
    </>
  );
};

export default VideoGallery;